"use client";

import React, { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { useAuth } from "../contexts/AuthContext";
import { publicRoutes } from "../utils/usePermissions";
import LoadingAnimation from "./common/loadingAnimation";
import { isLogoutInProgress } from "../oidc-client-auth/redirectGuard";

const fullPageLoading = (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingAnimation />
  </div>
);

/**
 * Prevents protected pages from rendering until auth state is known.
 * For protected routes: shows full-page loading until authenticated, then redirects to login
 * without ever mounting the page content. This avoids the flash of protected content when
 * an unauthenticated user lands on a URL directly.
 */
export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { isLoading, isAuthenticated, login } = useAuth();
  const loginStartedRef = useRef(false);

  const isPublicRoute = publicRoutes.includes(router.pathname);

  useEffect(() => {
    if (isPublicRoute) return;
    if (isLoading) return;
    if (isAuthenticated) return;
    // Don't redirect to login when we're in the same tab and just triggered logout
    // (logout sets user to null before window.location.assign, which would race with this).
    if (isLogoutInProgress()) return;
    if (loginStartedRef.current) return;
    loginStartedRef.current = true;
    void login(router.asPath);
  }, [isPublicRoute, isLoading, isAuthenticated, login, router.asPath]);

  // Public routes: always render children (page handles its own auth/redirect if needed)
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // Protected routes: block render until we know auth; if not authenticated, redirect will happen
  if (isLoading) return fullPageLoading;
  if (!isAuthenticated) return fullPageLoading;

  return <>{children}</>;
}
